import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Rnd } from "react-rnd";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Trash2,
  Save,
  ShirtIcon,
  Palette,
  Phone,
  DollarSign,
  Store as StoreIcon,
  Image as ImageIcon,
  Crop,
} from "lucide-react";

import { api, readFileAsBase64, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PRINT_SCALES = [15, 20, 30, 40, 50, 70, 100];

const TSHIRT_CATEGORIES = [
  { key: "masculino", label: "Masculino" },
  { key: "feminino", label: "Feminino" },
  { key: "cropped", label: "Cropped" },
  { key: "infantil", label: "Infantil" },
];

const PRINT_ARTISTS = [
  { key: "odilea", label: "Odiléa Toscano" },
  { key: "orlando", label: "Orlando Setti" },
];
import { Separator } from "@/components/ui/separator";

export default function Admin() {
  const [config, setConfig] = useState({
    whatsapp: "",
    price: 0,
    shop_name: "Estampa Livre",
  });
  const [tshirts, setTshirts] = useState([]);
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadAll = async () => {
    const [c, t, p] = await Promise.all([
      api.get("/config"),
      api.get("/tshirts"),
      api.get("/prints"),
    ]);
    setConfig(c.data);
    setTshirts(t.data);
    setPrints(p.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadAll();
      } catch {
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await api.put("/config", {
        whatsapp: config.whatsapp || "",
        price: Number(config.price) || 0,
        shop_name: config.shop_name || "Estampa Livre",
      });
      setConfig(res.data);
      toast.success("Configurações salvas!");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSavingConfig(false);
    }
  };

  const uploadMedia = async (endpoint, file, extra = {}) => {
    const b64 = await readFileAsBase64(file);
    const res = await api.post(endpoint, {
      name: file.name.replace(/\.[^.]+$/, ""),
      image_base64: b64,
      ...extra,
    });
    return res.data;
  };

  const handleTshirtUpload = async (files, category) => {
    try {
      const added = [];
      for (const f of files) {
        const item = await uploadMedia("/tshirts", f, { category });
        added.push(item);
      }
      setTshirts((prev) => [...added, ...prev]);
      toast.success(`${added.length} camiseta(s) enviada(s)`);
    } catch {
      toast.error("Falha no upload da camiseta");
    }
  };

  const handlePrintUpload = async (files, category) => {
    try {
      const added = [];
      for (const f of files) {
        const item = await uploadMedia("/prints", f, { category });
        added.push(item);
      }
      setPrints((prev) => [...added, ...prev]);
      toast.success(`${added.length} estampa(s) enviada(s)`);
    } catch {
      toast.error("Falha no upload da estampa");
    }
  };

  const deleteTshirt = async (id) => {
    try {
      await api.delete(`/tshirts/${id}`);
      setTshirts((prev) => prev.filter((t) => t.id !== id));
      toast.success("Camiseta removida");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const savePrintArea = async (id, printArea) => {
    try {
      const res = await api.patch(`/tshirts/${id}`, { print_area: printArea });
      setTshirts((prev) => prev.map((t) => (t.id === id ? res.data : t)));
      toast.success("Área de impressão salva");
    } catch {
      toast.error("Erro ao salvar área");
    }
  };

  const deletePrint = async (id) => {
    try {
      await api.delete(`/prints/${id}`);
      setPrints((prev) => prev.filter((t) => t.id !== id));
      toast.success("Estampa removida");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const savePrintScale = async (id, scale) => {
    try {
      const res = await api.patch(`/prints/${id}`, { scale });
      setPrints((prev) => prev.map((p) => (p.id === id ? res.data : p)));
      toast.success("Escala salva");
    } catch {
      toast.error("Erro ao salvar escala");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Carregando painel...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gray-900 flex items-center justify-center">
              <StoreIcon className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Painel do Vendedor</h1>
              <p className="text-xs text-gray-500">
                Cadastre camisetas, estampas e configure seu WhatsApp
              </p>
            </div>
          </div>
          <Link
            to="/"
            className="text-sm text-gray-600 hover:text-[#FF5722] flex items-center gap-2"
            data-testid="back-to-store-link"
          >
            <ArrowLeft size={16} /> Ver a loja
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Config card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Configurações</h2>
          <p className="text-sm text-gray-500 mb-6">
            Essas informações aparecem para o cliente e são usadas no envio do pedido.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="shop">
                <span className="inline-flex items-center gap-1">
                  <StoreIcon size={12} /> Nome da loja
                </span>
              </Label>
              <Input
                id="shop"
                value={config.shop_name || ""}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, shop_name: e.target.value }))
                }
                data-testid="shop-name-input"
                placeholder="Estampa Livre"
              />
            </div>
            <div>
              <Label htmlFor="whatsapp">
                <span className="inline-flex items-center gap-1">
                  <Phone size={12} /> WhatsApp (com DDD/DDI)
                </span>
              </Label>
              <Input
                id="whatsapp"
                value={config.whatsapp || ""}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, whatsapp: e.target.value }))
                }
                data-testid="whatsapp-input"
                placeholder="Ex.: 5511999999999"
              />
            </div>
            <div>
              <Label htmlFor="price">
                <span className="inline-flex items-center gap-1">
                  <DollarSign size={12} /> Preço unitário (R$)
                </span>
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={config.price ?? 0}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, price: e.target.value }))
                }
                data-testid="price-input"
                placeholder="79.90"
              />
              <p className="text-xs text-gray-500 mt-1">
                Preview: {brl(Number(config.price) || 0)}
              </p>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={saveConfig}
              disabled={savingConfig}
              className="brand-btn"
              data-testid="save-config-btn"
            >
              <Save size={16} /> {savingConfig ? "Salvando..." : "Salvar configurações"}
            </Button>
          </div>
        </div>

        {/* T-shirts */}
        <TshirtManager
          items={tshirts}
          onUpload={handleTshirtUpload}
          onDelete={deleteTshirt}
          onSavePrintArea={savePrintArea}
        />

        {/* Prints */}
        <ArtistPrintManager
          items={prints}
          onUpload={handlePrintUpload}
          onDelete={deletePrint}
          onSaveScale={savePrintScale}
        />

        <Separator />
        <p className="text-xs text-gray-500">
          As imagens são armazenadas em base64 no banco de dados.
        </p>
      </main>
    </div>
  );
}

function MediaManager({
  title,
  subtitle,
  icon,
  items,
  onUpload,
  onDelete,
  testidPrefix,
  checker = false,
}) {
  const inputRef = useRef(null);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-50 flex items-center justify-center text-[#FF5722]">
            {icon}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid={`${testidPrefix}-file-input`}
            onChange={(e) => {
              if (e.target.files?.length) {
                onUpload(Array.from(e.target.files));
                e.target.value = "";
              }
            }}
          />
          <Button
            onClick={() => inputRef.current?.click()}
            className="brand-btn"
            data-testid={`${testidPrefix}-upload-btn`}
          >
            <Upload size={16} /> Enviar imagens
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-gray-300 rounded-xl">
          <ImageIcon className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Nenhum item cadastrado ainda. Clique em &quot;Enviar imagens&quot;.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="group relative border border-gray-200 rounded-xl overflow-hidden"
              data-testid={`${testidPrefix}-card-${item.id}`}
            >
              <div
                className={`aspect-square ${
                  checker ? "checker-bg" : "bg-gray-50"
                } flex items-center justify-center`}
              >
                <img
                  src={item.image_base64}
                  alt={item.name}
                  className="w-full h-full object-contain p-2"
                />
              </div>
              <div className="p-2 flex items-center justify-between gap-2 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-700 truncate">
                  {item.name}
                </span>
                <button
                  onClick={() => onDelete(item.id)}
                  className="text-gray-400 hover:text-red-600"
                  data-testid={`${testidPrefix}-delete-${item.id}`}
                  title="Remover"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TshirtManager({ items, onUpload, onDelete, onSavePrintArea }) {
  const [activeCategory, setActiveCategory] = useState("masculino");
  const [editingId, setEditingId] = useState(null);
  const inputRef = useRef(null);

  const filtered = items.filter(
    (i) => (i.category || "masculino") === activeCategory
  );

  const editingItem = items.find((i) => i.id === editingId) || null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-50 flex items-center justify-center text-[#FF5722]">
            <ShirtIcon size={20} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Camisetas</h3>
            <p className="text-sm text-gray-500">
              Organize por categoria: Masculino, Feminino e Infantil.
            </p>
          </div>
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid={`tshirt-file-input-${activeCategory}`}
            onChange={(e) => {
              if (e.target.files?.length) {
                onUpload(Array.from(e.target.files), activeCategory);
                e.target.value = "";
              }
            }}
          />
          <Button
            onClick={() => inputRef.current?.click()}
            className="brand-btn"
            data-testid={`tshirt-upload-btn-${activeCategory}`}
          >
            <Upload size={16} /> Enviar para{" "}
            {
              TSHIRT_CATEGORIES.find((c) => c.key === activeCategory)?.label
            }
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {TSHIRT_CATEGORIES.map((c) => {
          const count = items.filter(
            (i) => (i.category || "masculino") === c.key
          ).length;
          const active = activeCategory === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setActiveCategory(c.key)}
              data-testid={`tshirt-admin-tab-${c.key}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                active
                  ? "border-[#FF5722] text-[#FF5722]"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {c.label}
              <span className="ml-1.5 text-xs text-gray-400">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-gray-300 rounded-xl">
          <ImageIcon className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Nenhuma camiseta nesta categoria ainda.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="group relative border border-gray-200 rounded-xl overflow-hidden"
              data-testid={`tshirt-card-${item.id}`}
            >
              <div className="aspect-square bg-gray-50 flex items-center justify-center relative">
                <img
                  src={item.image_base64}
                  alt={item.name}
                  className="w-full h-full object-contain p-2"
                />
                {item.print_area && (
                  <div
                    className="absolute pointer-events-none border-2 border-dashed border-[#FF5722]/60"
                    style={{
                      left: `${item.print_area.leftPct}%`,
                      top: `${item.print_area.topPct}%`,
                      width: `${item.print_area.widthPct}%`,
                      height: `${item.print_area.heightPct}%`,
                    }}
                  />
                )}
                <button
                  onClick={() => setEditingId(item.id)}
                  className="absolute bottom-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-gray-200 flex items-center justify-center text-gray-700 hover:text-[#FF5722] shadow-sm opacity-0 group-hover:opacity-100 transition"
                  title="Editar área de impressão"
                  data-testid={`tshirt-edit-area-${item.id}`}
                >
                  <Crop size={14} />
                </button>
              </div>
              <div className="p-2 flex items-center justify-between gap-2 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-700 truncate">
                  {item.name}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingId(item.id)}
                    className="text-gray-400 hover:text-[#FF5722]"
                    data-testid={`tshirt-edit-area-btn-${item.id}`}
                    title="Editar área"
                  >
                    <Crop size={14} />
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="text-gray-400 hover:text-red-600"
                    data-testid={`tshirt-delete-${item.id}`}
                    title="Remover"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PrintAreaEditor
        item={editingItem}
        onClose={() => setEditingId(null)}
        onSave={(area) => {
          onSavePrintArea(editingId, area);
          setEditingId(null);
        }}
      />
    </div>
  );
}

function PrintAreaEditor({ item, onClose, onSave }) {
  const [stageEl, setStageEl] = useState(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [box, setBox] = useState(null); // px: {x,y,w,h}

  useEffect(() => {
    if (!stageEl) {
      setStageSize({ w: 0, h: 0 });
      return;
    }
    const measure = () => {
      const r = stageEl.getBoundingClientRect();
      if (r.width > 0) setStageSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl]);

  useEffect(() => {
    if (!item || !stageSize.w) return;
    const src = item.print_area || {
      leftPct: 32.5,
      topPct: 32.5,
      widthPct: 35,
      heightPct: 35,
    };
    setBox({
      x: (src.leftPct / 100) * stageSize.w,
      y: (src.topPct / 100) * stageSize.h,
      w: (src.widthPct / 100) * stageSize.w,
      h: (src.heightPct / 100) * stageSize.h,
    });
  }, [item?.id, stageSize.w, stageSize.h]);

  const areaPct = useMemo(() => {
    if (!box || !stageSize.w) return null;
    return {
      leftPct: (box.x / stageSize.w) * 100,
      topPct: (box.y / stageSize.h) * 100,
      widthPct: (box.w / stageSize.w) * 100,
      heightPct: (box.h / stageSize.h) * 100,
    };
  }, [box, stageSize]);

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-full">
        <DialogHeader>
          <DialogTitle>Área de impressão</DialogTitle>
          <DialogDescription>
            Ajuste o retângulo pontilhado para definir onde a estampa será
            posicionada nesta camiseta. As estampas dos clientes serão
            redimensionadas automaticamente dentro dessa área.
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div
            ref={setStageEl}
            className="relative w-full aspect-square rounded-lg overflow-hidden bg-gray-50 border border-gray-200"
          >
            <img
              src={item.image_base64}
              alt={item.name}
              className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              draggable={false}
            />
            {box && stageSize.w > 0 && (
              <Rnd
                size={{ width: box.w, height: box.h }}
                position={{ x: box.x, y: box.y }}
                bounds="parent"
                onDragStop={(_, d) =>
                  setBox((b) => ({ ...b, x: d.x, y: d.y }))
                }
                onResizeStop={(_, __, ref, ___, position) =>
                  setBox({
                    x: position.x,
                    y: position.y,
                    w: parseFloat(ref.style.width),
                    h: parseFloat(ref.style.height),
                  })
                }
                className="border-2 border-dashed border-[#FF5722] bg-[#FF5722]/5"
              />
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="cancel-area-btn">
            Cancelar
          </Button>
          <Button
            className="brand-btn"
            onClick={() => areaPct && onSave(areaPct)}
            data-testid="save-area-btn"
          >
            <Save size={14} /> Salvar área
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArtistPrintManager({ items, onUpload, onDelete }) {
  const [activeArtist, setActiveArtist] = useState("odilea");
  const inputRef = useRef(null);

  const filtered = items.filter(
    (i) => (i.category || "odilea") === activeArtist
  );

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-50 flex items-center justify-center text-[#FF5722]">
            <Palette size={20} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Estampas</h3>
            <p className="text-sm text-gray-500">
              Organize as estampas por artista. Envie PNGs com fundo transparente.
            </p>
          </div>
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid={`print-file-input-${activeArtist}`}
            onChange={(e) => {
              if (e.target.files?.length) {
                onUpload(Array.from(e.target.files), activeArtist);
                e.target.value = "";
              }
            }}
          />
          <Button
            onClick={() => inputRef.current?.click()}
            className="brand-btn"
            data-testid={`print-upload-btn-${activeArtist}`}
          >
            <Upload size={16} /> Enviar para{" "}
            {PRINT_ARTISTS.find((a) => a.key === activeArtist)?.label}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {PRINT_ARTISTS.map((a) => {
          const count = items.filter(
            (i) => (i.category || "odilea") === a.key
          ).length;
          const active = activeArtist === a.key;
          return (
            <button
              key={a.key}
              onClick={() => setActiveArtist(a.key)}
              data-testid={`print-admin-tab-${a.key}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                active
                  ? "border-[#FF5722] text-[#FF5722]"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              Artista {a.label}
              <span className="ml-1.5 text-xs text-gray-400">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-gray-300 rounded-xl">
          <ImageIcon className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            Nenhuma estampa deste artista ainda.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="group relative border border-gray-200 rounded-xl overflow-hidden"
              data-testid={`print-card-${item.id}`}
            >
              <div className="aspect-square checker-bg flex items-center justify-center">
                <img
                  src={item.image_base64}
                  alt={item.name}
                  className="w-full h-full object-contain p-2"
                />
              </div>
              <div className="p-2 flex items-center justify-between gap-2 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-700 truncate">
                  {item.name}
                </span>
                <button
                  onClick={() => onDelete(item.id)}
                  className="text-gray-400 hover:text-red-600"
                  data-testid={`print-delete-${item.id}`}
                  title="Remover"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

